
import { Step2ValueModel, Step3ValueModel } from './types'
import {
    validateChangeOwner,
    validateChangeOwnerDate,
    validateLegalName,
    validateAccountType,
    validateBg,
    validatePg,
    validateStreetBox,
    validateZipCode,
    validateCity,
    validateMunicipality,
    validateVisitingAddressStreetAddress,
    validateVisitingAddressZipCode,
    validateVisitingAddressCity,
    validateVat,
    validateDonateAccountType,
    validateDonatePg,
    validateDonateBg,
} from '../shared/Validation'
import { AccountType } from '../shared/types'
import { FormErrorType } from "@sharedComponents/PantaForm/types";
import i18n from "@apps/Shared/translation/i18n";
import {
    appendError,
    removeAllErrorsForKey,
    removeError,
    validateIsNumber, validateIsZipCode,
    validateRequired
} from "@sharedComponents/PantaForm/utils";

export const validateCampsiteName = (
    campsiteName: string | undefined,
    currentErrors: FormErrorType[]
) => {
    const errors = validateRequired(
        campsiteName,
        'campsiteName',
        i18n.t('error.emptyCampsiteName'),
        currentErrors
    )

    return errors
}

export const validateCharity = (
    charity: string | undefined,
    currentErrors: FormErrorType[]
) => {
    return validateRequired(
        charity,
        'charity',
        i18n.t('error.emptyCharity'),
        currentErrors
    )
}

export const validateDonateName = (
    donateName: string | undefined,
    shouldValidate: boolean,
    currentErrors: FormErrorType[]
) => {
    if (!shouldValidate) {
        return removeAllErrorsForKey(currentErrors, 'donateName')
    }

    let errors = [...currentErrors]

    errors = validateRequired(
        donateName,
        'donateName',
        i18n.t('error.emptyDonateName'),
        currentErrors
    )
    if (donateName) {
        if (donateName.length > 50) {
            errors = appendError(
                errors,
                'donateName',
                i18n.t('error.donateNameToLong')
            )
        } else {
            errors = removeError(
                errors,
                'donateName',
                i18n.t('error.donateNameToLong')
            )
        }
    }
    return errors
}

export const validateDonateOrganisationNumber = (
    organizationNumber: string | undefined,
    shouldValidate: boolean,
    shouldValidateFormat: boolean,
    currentErrors: FormErrorType[]
) => {
    if (!shouldValidate) {
        return removeAllErrorsForKey(currentErrors, 'donateOrganisationNumber')
    }

    let errors = [...currentErrors]

    errors = validateRequired(
        organizationNumber,
        'donateOrganisationNumber',
        i18n.t('error.emptyDonateOrgnr'),
        currentErrors
    )

    if (organizationNumber) {
        if (shouldValidateFormat) {
            if (organizationNumber.length !== 10) {
                errors = appendError(
                    errors,
                    'donateOrganisationNumber',
                    i18n.t('error.invalidDonateOrgnr')
                )
            } else {
                errors = validateIsNumber(
                    organizationNumber,
                    'donateOrganisationNumber',
                    i18n.t('error.invalidDonateOrgnr'),
                    currentErrors
                )
            }
        } else {
            errors = removeError(
                errors,
                'donateOrganisationNumber',
                i18n.t('error.invalidDonateOrgnr')
            )
        }
    } else {
        errors = removeError(
            errors,
            'donateOrganisationNumber',
            i18n.t('error.invalidDonateOrgnr')
        )
    }

    return errors
}

// Postal Address Validation
export const validatePostalAddressStreetAddress = (
    postalAddressStreetAddress: string | undefined,
    shouldValidate: boolean,
    currentErrors: FormErrorType[]
) => {
    if (!shouldValidate) {
        return removeAllErrorsForKey(
            currentErrors,
            'postalAddress.streetAddress'
        )
    }

    return validateRequired(
        postalAddressStreetAddress,
        'postalAddress.streetAddress',
        i18n.t('error.emptyStreetAddress'),
        currentErrors
    )
}

export const validatePostalAddressCity = (
    postalAddressCity: string | undefined,
    shouldValidate: boolean,
    currentErrors: FormErrorType[]
) => {
    if (!shouldValidate) {
        return removeAllErrorsForKey(currentErrors, 'postalAddress.city')
    }

    return validateRequired(
        postalAddressCity,
        'postalAddress.city',
        i18n.t('error.emptyCity'),
        currentErrors
    )
}

export const validatePostalAddressZipCode = (
    postalAddressZipCode: string | undefined,
    shouldValidate: boolean,
    validateFormat: boolean,
    currentErrors: FormErrorType[]
) => {
    if (!shouldValidate) {
        return removeAllErrorsForKey(currentErrors, 'postalAddress.zipCode')
    }

    let errors = validateRequired(
        postalAddressZipCode,
        'postalAddress.zipCode',
        i18n.t('error.emptyZipCode'),
        currentErrors
    )

    if (postalAddressZipCode) {
        if (validateFormat) {
            errors = validateIsZipCode(
                postalAddressZipCode,
                'postalAddress.zipCode',
                i18n.t('error.incorrectZipCode'),
                currentErrors
            )
        } else {
            return removeAllErrorsForKey(currentErrors, 'postalAddress.zipCode')
        }
    }

    return errors
}

// Delivery Address Validation
export const validateDeliveryAddressStreetAddress1 = (
    deliveryAddressStreetAddress1: string | undefined,
    shouldValidate: boolean,
    currentErrors: FormErrorType[]
) => {
    if (!shouldValidate) {
        return removeAllErrorsForKey(
            currentErrors,
            'deliveryAddress.streetAddress1'
        )
    }

    return validateRequired(
        deliveryAddressStreetAddress1,
        'deliveryAddress.streetAddress1',
        i18n.t('error.emptyStreetAddress'),
        currentErrors
    )
}

export const validateDeliveryAddressCity = (
    deliveryAddressCity: string | undefined,
    shouldValidate: boolean,
    currentErrors: FormErrorType[]
) => {
    if (!shouldValidate) {
        return removeAllErrorsForKey(currentErrors, 'deliveryAddress.city')
    }

    return validateRequired(
        deliveryAddressCity,
        'deliveryAddress.city',
        i18n.t('error.emptyCity'),
        currentErrors
    )
}

export const validateDeliveryAddressZipCode = (
    deliveryAddressZipCode: string | undefined,
    shouldValidate: boolean,
    validateFormat: boolean,
    currentErrors: FormErrorType[]
) => {
    if (!shouldValidate) {
        return removeAllErrorsForKey(currentErrors, 'deliveryAddress.zipCode')
    }

    let errors = validateRequired(
        deliveryAddressZipCode,
        'deliveryAddress.zipCode',
        i18n.t('error.emptyZipCode'),
        currentErrors
    )

    if (deliveryAddressZipCode) {
        if (validateFormat) {
            errors = validateIsZipCode(
                deliveryAddressZipCode,
                'deliveryAddress.zipCode',
                i18n.t('error.incorrectZipCode'),
                currentErrors
            )
        } else {
            return removeAllErrorsForKey(
                currentErrors,
                'deliveryAddress.zipCode'
            )
        }
    }

    return errors
}

export const validateStep2 = async (
    stepValues: Step2ValueModel,
    currentErrors: FormErrorType[]
) => {
    let errors = [...currentErrors]

    errors = validateChangeOwner(stepValues.changeOwner, errors)
    errors = validateChangeOwnerDate(
        stepValues.changeOwnerDate,
        stepValues.changeOwner === 'true',
        errors
    )
    errors = validateLegalName(stepValues.legalName, errors)
    errors = validateCharity(stepValues.charity, errors)

    const shouldValidateDonate = stepValues.charity === 'true'

    errors = validateDonateName(
        stepValues.donateName,
        shouldValidateDonate,
        errors
    )
    errors = validateDonateOrganisationNumber(
        stepValues.donateOrganisationNumber,
        shouldValidateDonate,
        true,
        errors
    )
    errors = validateDonateAccountType(
        stepValues.donateAccountType,
        shouldValidateDonate,
        errors
    )
    errors = validateDonateBg(
        stepValues.donateBg,
        stepValues.donateAccountType === AccountType.Bg,
        errors
    )
    errors = validateDonatePg(
        stepValues.donatePg,
        stepValues.donateAccountType === AccountType.Pg,
        errors
    )

    if (stepValues.registeredForVAT === 'true') {
        errors = validateVat(stepValues.vatNumber, errors)
    }

    errors = validateAccountType(stepValues.accountType, true, errors)
    errors = validateBg(
        stepValues.bg,
        stepValues.accountType === AccountType.Bg,
        errors
    )
    errors = validatePg(
        stepValues.pg,
        stepValues.accountType === AccountType.Pg,
        errors
    )
    errors = validateStreetBox(stepValues.streetBox, errors)
    errors = validateZipCode(stepValues.zipCode, true, errors)
    errors = validateCity(stepValues.city, errors)

    return errors
}

export const validateStep3 = async (
    stepValues: Step3ValueModel,
    currentErrors: FormErrorType[]
) => {
    let errors = [...currentErrors]

    errors = validateCampsiteName(stepValues.campsiteName, errors)
    errors = validateMunicipality(stepValues.municipality, errors)
    errors = validateVisitingAddressStreetAddress(
        stepValues.visitingAddress.streetAddress,
        errors
    )
    errors = validateVisitingAddressZipCode(
        stepValues.visitingAddress.zipCode,
        true,
        errors
    )
    errors = validateVisitingAddressCity(
        stepValues.visitingAddress.city,
        errors
    )
    errors = validatePostalAddressStreetAddress(
        stepValues.postalAddress.streetAddress,
        stepValues.visitingAddress.isAlsoDeliveryAddress !== true,
        errors
    )
    errors = validatePostalAddressZipCode(
        stepValues.postalAddress.zipCode,
        stepValues.visitingAddress.isAlsoDeliveryAddress !== true,
        true,
        errors
    )
    errors = validatePostalAddressCity(
        stepValues.postalAddress.city,
        stepValues.visitingAddress.isAlsoDeliveryAddress !== true,
        errors
    )
    errors = validateDeliveryAddressStreetAddress1(
        stepValues.deliveryAddress.streetAddress1,
        stepValues.visitingAddress.isAlsoDeliveryAddress !== true,
        errors
    )
    errors = validateDeliveryAddressZipCode(
        stepValues.deliveryAddress.zipCode,
        stepValues.visitingAddress.isAlsoDeliveryAddress !== true,
        true,
        errors
    )
    errors = validateDeliveryAddressCity(
        stepValues.deliveryAddress.city,
        stepValues.visitingAddress.isAlsoDeliveryAddress !== true,
        errors
    )

    return errors
}